"use client";
import {
    Document,
    Page,
    Text,
    Image,
    StyleSheet,
    Link,
} from "@react-pdf/renderer";
import { pdf } from "@react-pdf/renderer";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

const styles = StyleSheet.create({
    page: {
        padding: 0,
        margin: 0,
        backgroundColor: "white",
        position: "relative",
        width: "100%",
    },
});

const Certification = ({ certification, creationDate, t }) => {
    return (
        <Document
            title={t("certification_title", {
                number: certification.certificationNumber,
            })}
            pageMode="fullscreen"
            pageLayout="singlePage"
            style={{
                width: "100%",
            }}
        >
            <Page size="A4" style={styles.page} orientation="landscape">
                <Image
                    src="/images/site/brand/certification.jpg"
                    alt="Certification"
                />
                <Text
                    style={{ position: "absolute", top: "250px", left: "49px" }}
                >
                    {certification.user.firstName} {certification.user.lastName}
                </Text>
                <Text
                    style={{
                        position: "absolute",
                        top: "330px",
                        left: "49px",
                        width: "370px",
                    }}
                >
                    {certification.course.category == "course" ? (
                        <Link
                            href={`https://believemy.com/course/${certification.course.slug}`}
                            style={{
                                color: "#000000",
                                textDecoration: "none",
                                lineHeight: "1.5",
                            }}
                        >
                            {certification.course.name}
                        </Link>
                    ) : (
                        certification.course.name
                    )}
                </Text>
                <Text
                    style={{
                        position: "absolute",
                        top: "535px",
                        right: "49px",
                        fontSize: "11px",
                    }}
                >
                    ID:{" "}
                    <Text style={{ fontWeight: "bold" }}>
                        {certification.certificationNumber}
                    </Text>{" "}
                    • Issued on{" "}
                    <Text style={{ fontWeight: "bold" }}>{creationDate}</Text>
                </Text>
            </Page>
        </Document>
    );
};

export default function CertificationDocument({ certification, creationDate }) {
    const t = useTranslations();
    const [imgUrl, setImgUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const doc = useMemo(() => {
        if (!certification || !certification.user || !certification.course)
            return null;
        return (
            <Certification
                certification={certification}
                creationDate={creationDate}
                t={t}
            />
        );
    }, [certification, creationDate]);

    useEffect(() => {
        let canceled = false;
        if (!doc) return;

        setIsLoading(true);

        (async () => {
            try {
                // 1) Génère le PDF côté client
                const blob = await pdf(doc).toBlob();
                const arrayBuffer = await blob.arrayBuffer();

                // 2) Charge PDF.js à la volée et attache un worker local (pas de CDN)
                const pdfjsLib = await import("pdfjs-dist");
                if (
                    pdfjsLib?.GlobalWorkerOptions &&
                    !pdfjsLib.GlobalWorkerOptions.workerPort
                ) {
                    try {
                        // Attache un Module Worker packagé par Next (pas de fetch public requis)
                        const moduleUrl = new URL(
                            "pdfjs-dist/build/pdf.worker.mjs",
                            import.meta.url,
                        );
                        const worker = new Worker(moduleUrl, {
                            type: "module",
                        });
                        pdfjsLib.GlobalWorkerOptions.workerPort = worker;
                    } catch (_) {
                        // Laisser pdf.js utiliser le fake worker en dernier recours
                    }
                }

                // 3) Ouvre le document et rend la 1ère page sur un canvas
                const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                const pdfDocument = await loadingTask.promise;
                const page = await pdfDocument.getPage(1);

                const scale = 3; // qualité de rendu (3x)
                const viewport = page.getViewport({ scale });

                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: context, viewport }).promise;

                if (canceled) return;

                const dataUrl = canvas.toDataURL("image/png");
                setImgUrl(dataUrl);
            } catch (error) {
                console.error(
                    "Erreur lors de la génération de l'image:",
                    error,
                );
            } finally {
                if (!canceled) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            canceled = true;
        };
    }, [doc]);

    if (!doc) return <div>Invalid data</div>;

    return (
        <div className="aspect-[1.4142857142857142/1] overflow-hidden relative rounded-xl">
            <div
                className={`absolute inset-0 bg-gray-50 dark:bg-bgDark-700 transition-opacity duration-500 ${
                    !isLoading ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
            >
                <div className="w-full h-full flex items-center justify-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></div>
                    <span className="text-sm text-gray-500 dark:text-textSecondDark font-medium">
                        Document en préparation…
                    </span>
                </div>
            </div>

            {imgUrl && (
                <img
                    src={imgUrl}
                    alt="Certification"
                    className={`w-full h-auto transition-opacity duration-700 ease-out ${
                        !isLoading ? "opacity-100" : "opacity-0"
                    }`}
                    style={{ aspectRatio: "1.4142857142857142/1" }}
                    onLoad={() => setIsLoading(false)}
                />
            )}
        </div>
    );
}
